import type { TourEvent } from '@/features/events';
import { formatDateRangeHero, machineDate } from '@/features/events';
import { Brandmark, DisplayTitle, Eyebrow } from '@/components/typography';
import { ResponsiveImage, VideoDialog } from '@/components/media';
import { cancunMedia } from '../media';
import { CANCUN_COPY } from '../copy';
import { SeaArc } from '../ornaments/SeaArc';
import { FlameDrop } from '../ornaments/FlameDrop';
import { CancunCrown } from '../ornaments/CancunCrown';
import styles from './HeroCancun.module.css';
import { VslInline } from '@/pages/shared/VslInline';
import { HeroReservaCta } from '@/pages/shared/ConversionBlocks';
import { MigaGira } from '@/pages/shared/MigaGira';

export const HERO_TITLE_ID = 'cancun-hero-titulo';

/**
 * "LEVEL UP: El Despertar del Guerrero — Conciencia y Transformación"
 * -> grito dorado + respuesta serena.
 * (El poster rotula «El Despertar del Guerrero»: lo cambio el cliente el
 *  28-jul-2026. Manda el cliente sobre su propio evento. ADR-006.)
 * El corte sale del propio título del dato, no de dos cadenas tecleadas aquí.
 */
function splitTitle(title: string): { shout: string; answer: string } {
  const colon = title.indexOf(':');
  if (colon < 0) return { shout: title, answer: '' };
  return { shout: title.slice(0, colon + 1), answer: title.slice(colon + 1).trim() };
}

export interface HeroCancunProps {
  event: TourEvent;
}

/**
 * S1 · HERO FRENTE AL MAR — `DEMO_5_CancunVIP.png`, banda y 0-610.
 *
 * Todo el bloque tipográfico va CENTRADO (a diferencia de México, que tiene
 * columna lateral), con el retrato integrado detrás y a la derecha. El bloque
 * grande cede ~3 % a la izquierda para no chocar con el hombro de Azrahel:
 * esa asimetría es del mockup y se recupera a 0 por debajo de 1024.
 *
 * El venue va bajo "RETIRO VIVENCIAL PREMIUM · COMITÉ ÍNTIMO" como texto, sin
 * dirección postal ni mapa (decisión del ORACLE + ADR-003 §2).
 */
export function HeroCancun({ event }: HeroCancunProps) {
  const { shout, answer } = splitTitle(event.titleMain);

  return (
    <section className={styles.hero} aria-labelledby={HERO_TITLE_ID}>
      {/* Vuelta al hub sin abrir el menú, en todas las ciudades y a todos los
          anchos (encargo del cliente, 25-ago-2026). */}
      <MigaGira event={event} className={styles.miga} />
      <div className={styles.inner}>
        <div className={styles.brandBlock}>
          {/* El emblema oficial viene de serie en `Brandmark` desde el
              manifiesto (ADR-002): la página ya no lo inyecta. */}
          <Brandmark layout="stacked" size="md" eager />
          {/* Corona LOCAL: `VipCrown` del substrato está denegado y además no es
              la misma corona que el póster (5 puntas con aro de base capilar
              frente a 3 macizas). El porqué, medido, en `CancunCrown.tsx`. */}
          <CancunCrown className={styles.crown} />
        </div>

        <div className={styles.group}>
          <Eyebrow align="center" className={styles.place}>
            {event.city}
          </Eyebrow>

          {event.isVip ? (
            <p className={styles.vip}>
              <span className={styles.vipRule} />
              {/* LA NUBE VA EN LA PALABRA, NO EN EL RENGLÓN. «VIP» es oro
                  pequeño sobre la fotografía, y el contraste se mide contra lo
                  que hay DEBAJO del glifo (FINDING-004): medido a 320 y 390 px
                  daba 4,32:1 y 4,36:1, por debajo del 4,5 de AA. `az-on-photo`
                  hace el fondo determinista. En el `<p>` no, porque ahí la nube
                  abarcaría también los dos filetes y se leería como caja. */}
              <span className="az-on-photo">{CANCUN_COPY.vipLabel}</span>
              <span className={styles.vipRule} />
            </p>
          ) : null}

          {/* AFORO — encargo del cliente, 26-ago-2026, y pidió que se viera.
              Va en chapa propia y no como una línea más de texto: en el hero
              hay ya cuatro rótulos pequeños seguidos (ciudad, VIP, fechas,
              formato) y un quinto se pierde entre ellos. La chapa es lo único
              con fondo en toda la columna, así que es donde cae el ojo. */}
          <p className={styles.aforo}>{CANCUN_COPY.aforo}</p>
        </div>

        <div className={styles.group}>
          <DisplayTitle id={HERO_TITLE_ID} level={1} ink="gold" align="center" className={styles.title}>
            <span className={styles.shout}>{shout}</span>
            {answer ? <span className={styles.answer}>{answer}</span> : null}
          </DisplayTitle>
        </div>

        <div className={styles.group}>
          <p className={styles.date}>
            <span className={styles.dateRule} />
            <time dateTime={machineDate(event)}>{formatDateRangeHero(event)}</time>
            <span className={styles.dateRule} />
          </p>

          {/* SU VSL, ENCIMA DE LA PROMESA, igual que en las otras cuatro. Cancún
              lleva DOS vídeos y el ORACLE lo dejó claro el 12-sep-2026: «Cancún
              tiene 2, uno del botón y otro del VSL». Éste explica el retiro; el
              de Palmaïa, detrás de su botón, ENSEÑA el hotel. */}
          <VslInline event={event} />

          <p className={styles.format}>
            {event.promiseLines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </p>

          {event.venue ? <p className={styles.venue}>{event.venue}</p> : null}

          {/* LA BANDERA VIENE DEL DATO, NO DE UN SVG.
           * `icons/flags/mx.svg` eran tres franjas verde/blanco/rojo sin
           * escudo — y una bandera de México sin escudo NO es una bandera de
           * México simplificada: es, trazo por trazo, la bandera de Italia.
           * Servida en la página del evento mexicano y encima rotulada
           * «Bandera de México» para el lector de pantalla. El escudo tampoco
           * se puede aproximar (doctrina del repo: un emblema nacional o se
           * dibuja exacto o no se dibuja), así que se pinta el mismo emoji que
           * el hub, que trae la bandera real y completa. Decorativo y
           * `aria-hidden`: la sede ya está escrita encima con todas sus letras.
           * (ORACLE + CENTINEL, 25-jul-2026.) */}
          <span className={styles.flag} aria-hidden="true">
            {event.flag}
          </span>

          {/* Botón de compra en el hero (encargo del cliente, 28-jul-2026). En
              Cancún va en `premium`: es la página VIP y su CTA es oro, no navy. */}
          <HeroReservaCta
            event={event}
            variant="premium"
            extra={
              <VideoDialog
                label={CANCUN_COPY.video.label}
                title={CANCUN_COPY.video.titulo}
                description={CANCUN_COPY.video.descripcion}
                videoId={CANCUN_COPY.video.bunnyId}
                variant="gold"
                size="lg"
              />
            }
          />
        </div>
      </div>

      <div className={styles.media}>
        <ResponsiveImage
          asset={cancunMedia.heroScene}
          sizes="100vw"
          className={styles.scene}
          loading="eager"
          fetchPriority="high"
        />

        {/* Cortina y hoja de palmera: las dos ENTRAN por el borde izquierdo, o
            sea que sus cajas sobresalen del viewport a propósito y `.media` las
            recorta. Van bajo un `aria-hidden` común y explícito: son decoración
            que sangra, y así el gate de desbordes del substrato —que perdona lo
            decorativo y NO perdona lo demás— sigue diciendo la verdad sobre esta
            página en vez de quedarse sin criterio. */}
        <span className={styles.decor} aria-hidden="true">
          {/* Cortina blanca con su anilla dorada: recorte con alfa real de
              FORGE-BRAVO. Antes era un degradado CSS porque la pieza no existía. */}
          <ResponsiveImage
            asset={cancunMedia.heroCurtain}
            sizes="(min-width: 48rem) 22vw, 34vw"
            className={styles.curtain}
            loading="eager"
            decorative
          />

          {/* Hoja de palmera a contraluz asomando por arriba-izquierda (mockup). */}
          <ResponsiveImage
            asset={cancunMedia.palmLeaf}
            sizes="(min-width: 48rem) 30vw, 46vw"
            className={styles.palm}
            loading="eager"
            decorative
          />
        </span>

        {/* El velo crema va AQUÍ, entre la escena y el retrato. Siendo
            `.inner::before` se pintaba por encima de `.media` y lavaba a
            Azrahel: el cuerpo se disolvía hacia abajo. Ahora lo tapa el propio
            retrato y el cuerpo llega entero hasta el arco, como en el mockup. */}
        <span className={styles.veil} aria-hidden="true" />

        <ResponsiveImage
          asset={cancunMedia.heroPortrait}
          sizes="(min-width: 48rem) 38vw, 58vw"
          className={styles.portrait}
          loading="eager"
        />
      </div>

      <SeaArc className={styles.arc} />

      {/* LA GOTA DE LLAMA CUELGA DEL ARCO, Y ANTES NO COLGABA DE NADA.
       * Vivía en `ProgramaCancun`, subida con un margen negativo, y medida caía
       * ENTERA por debajo del arco: a 1024 la curva pasa por y=867 en el eje y
       * la gota ocupaba 884..935, o sea 17 px por debajo del filete. En el
       * póster lo CRUZA — 77 % del dibujo por encima de la curva y 23 % por
       * debajo — y ése es todo el gesto: sin cruzarlo es un adorno suelto.
       *
       * No podía arreglarse desde el programa: esa sección lleva
       * `overflow: hidden`, así que un margen negativo mayor recortaba la gota
       * en vez de subirla. Desde el hero, `inset-block-end: 0` la deja a
       * 826..877 con la curva del eje en 867 → 80 % arriba / 20 % abajo.
       *
       * Va al EJE de la página (50 %) y no al vientre real de la curva (55,2 %):
       * el póster también la pone en el eje, aunque su propio arco tenga el
       * punto más bajo corrido a la derecha. Se calca el póster, no la teoría. */}
      <span className={styles.dropAnchor} aria-hidden="true">
        <FlameDrop size={34} />
      </span>
    </section>
  );
}
